/**
 * Public-video evidence link parsing for the RDTB whistleblower portal.
 *
 * SECURITY POSTURE — read before changing anything here:
 *
 *   1. ALLOW-LIST, NOT BLOCK-LIST. Only the five platforms the Department
 *      accepts are parseable. Anything else is refused at intake.
 *   2. THE RAW URL IS DISCARDED. We store {platform, videoId} and rebuild a
 *      canonical URL when displaying. A citizen-supplied string is therefore
 *      never replayed verbatim into an href, an iframe src, or a server-side
 *      fetch. That closes both stored-XSS and SSRF in one move.
 *   3. NOTHING HERE FETCHES. Thumbnail retrieval happens only against the fixed
 *      oEmbed endpoints in `OEMBED_ENDPOINTS`, from the edge function, never
 *      against a user-supplied host.
 *
 * Video links rot, and we cannot prevent it. A culprit can delete the post
 * after it is reported, and because this system stores no files, there is no
 * cached copy or thumbnail to fall back on. That is a real and accepted
 * limitation of the no-upload design.
 *
 * What we do instead: record {platform, videoId} plus the moment it was
 * submitted, and require the first officer who views it to record an
 * OBSERVATION describing what the video showed. Their attestation survives the
 * video's deletion, which is the only durable evidence available here.
 */
/**
 * Host allow-list. A URL whose hostname is not in this map is refused outright,
 * before any pattern matching runs.
 */
const ALLOWED_HOSTS = {
    'youtube.com': 'youtube',
    'www.youtube.com': 'youtube',
    'm.youtube.com': 'youtube',
    'youtu.be': 'youtube',
    'facebook.com': 'facebook',
    'www.facebook.com': 'facebook',
    'm.facebook.com': 'facebook',
    'web.facebook.com': 'facebook',
    'fb.watch': 'facebook',
    'twitter.com': 'x',
    'www.twitter.com': 'x',
    'mobile.twitter.com': 'x',
    'x.com': 'x',
    'www.x.com': 'x',
    'instagram.com': 'instagram',
    'www.instagram.com': 'instagram',
    'tiktok.com': 'tiktok',
    'www.tiktok.com': 'tiktok',
    'm.tiktok.com': 'tiktok',
    'vm.tiktok.com': 'tiktok',
    'vt.tiktok.com': 'tiktok',
};
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const NUMERIC_ID = /^\d{5,25}$/;
const SHORTCODE = /^[A-Za-z0-9_-]{5,32}$/;
function parseYouTube(url) {
    if (url.hostname === 'youtu.be') {
        const id = url.pathname.slice(1).split('/')[0] ?? '';
        return YOUTUBE_ID.test(id) ? id : null;
    }
    const v = url.searchParams.get('v');
    if (v && YOUTUBE_ID.test(v))
        return v;
    const segments = url.pathname.split('/').filter(Boolean);
    // /shorts/ID, /embed/ID, /live/ID, /v/ID
    if (segments.length >= 2 && ['shorts', 'embed', 'live', 'v'].includes(segments[0])) {
        const id = segments[1];
        return YOUTUBE_ID.test(id) ? id : null;
    }
    return null;
}
function parseFacebook(url) {
    if (url.hostname === 'fb.watch') {
        const code = url.pathname.split('/').filter(Boolean)[0] ?? '';
        return SHORTCODE.test(code) ? code : null;
    }
    const v = url.searchParams.get('v');
    if (v && NUMERIC_ID.test(v))
        return v;
    const segments = url.pathname.split('/').filter(Boolean);
    // /reel/ID  or  /{page}/videos/ID  or  /video.php?v=ID
    if (segments[0] === 'reel' && segments[1] && NUMERIC_ID.test(segments[1]))
        return segments[1];
    const vi = segments.indexOf('videos');
    if (vi !== -1 && segments[vi + 1] && NUMERIC_ID.test(segments[vi + 1])) {
        return segments[vi + 1];
    }
    return null;
}
function parseX(url) {
    const segments = url.pathname.split('/').filter(Boolean);
    const si = segments.indexOf('status');
    const id = si !== -1 ? segments[si + 1] : undefined;
    return id && NUMERIC_ID.test(id) ? id : null;
}
function parseInstagram(url) {
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments.length >= 2 && ['p', 'reel', 'reels', 'tv'].includes(segments[0])) {
        const code = segments[1];
        return SHORTCODE.test(code) ? code : null;
    }
    return null;
}
function parseTikTok(url) {
    if (url.hostname === 'vm.tiktok.com' || url.hostname === 'vt.tiktok.com') {
        const code = url.pathname.split('/').filter(Boolean)[0] ?? '';
        return SHORTCODE.test(code) ? code : null;
    }
    const segments = url.pathname.split('/').filter(Boolean);
    const vi = segments.indexOf('video');
    if (vi !== -1 && segments[vi + 1] && NUMERIC_ID.test(segments[vi + 1])) {
        return segments[vi + 1];
    }
    return null;
}
/**
 * Parse a citizen-supplied video URL. Returns null for anything that is not a
 * recognised public video on an allow-listed platform.
 *
 * A null return is not an error condition to log — it is the ordinary case of a
 * citizen pasting something unexpected, and the UI should say so plainly.
 */
export function parseVideoUrl(input) {
    const trimmed = input?.trim();
    if (!trimmed || trimmed.length > 2048)
        return null;
    let url;
    try {
        url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    }
    catch {
        return null;
    }
    // Scheme allow-list: no javascript:, data:, file:, ftp:
    if (url.protocol !== 'https:' && url.protocol !== 'http:')
        return null;
    const platform = ALLOWED_HOSTS[url.hostname.toLowerCase()];
    if (!platform)
        return null;
    let videoId = null;
    switch (platform) {
        case 'youtube':
            videoId = parseYouTube(url);
            break;
        case 'facebook':
            videoId = parseFacebook(url);
            break;
        case 'x':
            videoId = parseX(url);
            break;
        case 'instagram':
            videoId = parseInstagram(url);
            break;
        case 'tiktok':
            videoId = parseTikTok(url);
            break;
    }
    if (!videoId)
        return null;
    return { platform, videoId, canonicalUrl: buildCanonicalUrl(platform, videoId) };
}
/**
 * Rebuild a display URL from parsed parts.
 *
 * Facebook, Instagram and TikTok short codes cannot be turned back into a fully
 * canonical permalink without the author's handle, which we deliberately do not
 * store — so those resolve through the platform's own redirector.
 */
export function buildCanonicalUrl(platform, videoId) {
    switch (platform) {
        case 'youtube':
            return `https://www.youtube.com/watch?v=${videoId}`;
        case 'facebook':
            return NUMERIC_ID.test(videoId)
                ? `https://www.facebook.com/watch/?v=${videoId}`
                : `https://fb.watch/${videoId}`;
        case 'x':
            return `https://x.com/i/status/${videoId}`;
        case 'instagram':
            return `https://www.instagram.com/reel/${videoId}/`;
        case 'tiktok':
            return NUMERIC_ID.test(videoId)
                ? `https://www.tiktok.com/embed/v2/${videoId}`
                : `https://vm.tiktok.com/${videoId}`;
    }
}
/** Embeddable URL for a sandboxed iframe. */
export function buildEmbedUrl(platform, videoId) {
    switch (platform) {
        case 'youtube':
            return `https://www.youtube-nocookie.com/embed/${videoId}`;
        case 'facebook':
            return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(buildCanonicalUrl('facebook', videoId))}`;
        case 'instagram':
            return `https://www.instagram.com/reel/${videoId}/embed/`;
        case 'tiktok':
            return NUMERIC_ID.test(videoId) ? `https://www.tiktok.com/embed/v2/${videoId}` : null;
        case 'x':
            // X has no stable iframe embed without their widget script, which we do
            // not load on the officer console (CSP forbids third-party script).
            return null;
    }
}
/**
 * Fixed oEmbed endpoints — the ONLY hosts the edge function may fetch for a
 * thumbnail. Never construct a fetch target from user input.
 */
export const OEMBED_ENDPOINTS = {
    youtube: 'https://www.youtube.com/oembed',
    tiktok: 'https://www.tiktok.com/oembed',
};
/** YouTube exposes a deterministic thumbnail URL; the others require oEmbed. */
export function youtubeThumbnailUrl(videoId) {
    return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}
//# sourceMappingURL=video.js.map